import { useEffect } from "react";
import { Loader2, Sparkles, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

type Props = {
  open: boolean;
  title: string;
  loading: boolean;
  error: string | null;
  /** Narrative or structured-fallback markdown; null while loading/erroring. */
  markdown: string | null;
  /** Backend hint, e.g. "LLM gateway disabled — showing the structured summary." */
  note?: string | null;
  onClose: () => void;
};

/**
 * Read-only modal that renders a Cognos object's summary (the markdown
 * returned by /api/v1/content/summarize — either the LLM narrative or
 * the structured fallback the client builds when the gateway is off).
 */
export function SummaryModal({ open, title, loading, error, markdown, note, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-datumcraft-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Summary of ${title}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[85vh] w-[min(760px,95vw)] flex-col overflow-hidden rounded-2xl border border-datumcraft-slate/25 bg-white shadow-elev">
        <header className="flex items-center justify-between border-b border-datumcraft-slate/20 px-5 py-3">
          <h2 className="flex items-center gap-2 font-display text-base font-semibold text-datumcraft-navy">
            <Sparkles size={16} className="text-datumcraft-accentDark" aria-hidden />
            <span className="truncate">Summary — {title}</span>
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-ink"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-datumcraft-slate">
              <Loader2 size={16} className="animate-spin" aria-hidden />
              Reading the spec and summarizing…
            </div>
          ) : error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <>
              {note && (
                <p className="mb-3 rounded-md border border-datumcraft-slate/20 bg-datumcraft-mist/40 px-3 py-2 text-xs text-datumcraft-slate">
                  {note}
                </p>
              )}
              <article className="prose prose-sm max-w-none text-datumcraft-ink prose-headings:text-datumcraft-navy prose-strong:text-datumcraft-navy">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {markdown || "_No summary available._"}
                </ReactMarkdown>
              </article>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
